import Layout from "@/components/Layout";
import { SectionLabel, SectionTitle } from "@/components/Section";

const LastUpdated = () => (
  <p className="text-sm text-muted-foreground font-body mb-8">
    Last updated: May 24, 2026
  </p>
);

const SectionHeading = ({ children }: { children: React.ReactNode }) => (
  <h2 className="font-display text-xl text-navy-deep mt-8 mb-3">{children}</h2>
);

const Body = ({ children }: { children: React.ReactNode }) => (
  <p className="text-text-light font-body leading-relaxed mb-4">{children}</p>
);

const List = ({ items }: { items: string[] }) => (
  <ul className="list-disc list-inside text-text-light font-body leading-relaxed mb-4 space-y-1">
    {items.map((item, i) => (
      <li key={i}>{item}</li>
    ))}
  </ul>
);

const TableRow = ({ cookie, purpose, duration }: { cookie: string; purpose: string; duration: string }) => (
  <tr className="border-b border-border">
    <td className="py-3 px-4 text-sm font-body text-navy-deep">{cookie}</td>
    <td className="py-3 px-4 text-sm font-body text-text-light">{purpose}</td>
    <td className="py-3 px-4 text-sm font-body text-text-light">{duration}</td>
  </tr>
);

const Cookies = () => (
  <Layout>
    <div className="bg-gradient-to-b from-white via-off-white to-white min-h-screen">
      <div className="max-w-[900px] mx-auto px-6 lg:px-10 pt-32 pb-20">
        <SectionLabel>Legal</SectionLabel>
        <SectionTitle>Cookies Policy</SectionTitle>
        <LastUpdated />

        <SectionHeading>1. What Are Cookies</SectionHeading>
        <Body>
          Cookies are small text files that are placed on your computer or mobile device when you visit 
          a website. They are widely used to make websites work more efficiently and to provide information 
          to the owners of the site. Cookies can be "persistent" or "session" cookies. Persistent cookies 
          remain on your personal computer or mobile device when you go offline, while session cookies 
          are deleted as soon as you close your web browser.
        </Body>

        <SectionHeading>2. How We Use Cookies</SectionHeading>
        <Body>
          Velocity Offshore Holdings LLC uses cookies on our website at www.velocityoffshore.com (the "Site") 
          for the following purposes:
        </Body>
        <List items={[
          "Essential / Necessary Cookies: These cookies are necessary for the Site to function and cannot be switched off in our systems. They enable core functionality such as security, network management, and account access.",
          "Performance / Analytics Cookies: These cookies allow us to count visits and traffic sources so we can measure and improve the performance of our Site. They help us know which pages are the most and least popular and see how visitors move around the Site.",
          "Functionality Cookies: These cookies enable the Site to provide enhanced functionality and personalisation. They may be set by us or by third-party providers whose services we have added to our pages.",
        ]} />

        <SectionHeading>3. Cookies We Use</SectionHeading>
        <Body>
          The table below sets out the cookies we use on our Site and the purposes for which we use them:
        </Body>
        <div className="overflow-x-auto mb-6">
          <table className="w-full text-left border border-border rounded-lg">
            <thead>
              <tr className="bg-muted">
                <th className="py-3 px-4 text-sm font-display font-semibold text-navy-deep">Cookie Name</th>
                <th className="py-3 px-4 text-sm font-display font-semibold text-navy-deep">Purpose</th>
                <th className="py-3 px-4 text-sm font-display font-semibold text-navy-deep">Duration</th>
              </tr>
            </thead>
            <tbody>
              <TableRow cookie="cookie_consent" purpose="Records your cookie preferences" duration="1 year" />
              <TableRow cookie="session_token" purpose="Maintains your authenticated session on the Portals" duration="Session" />
              <TableRow cookie="_ga" purpose="Google Analytics — distinguishes unique users" duration="2 years" />
              <TableRow cookie="_gid" purpose="Google Analytics — distinguishes unique users for 24 hours" duration="24 hours" />
              <TableRow cookie="_gat" purpose="Google Analytics — throttles request rate" duration="1 minute" />
            </tbody>
          </table>
        </div>
        <Body>
          Please note that third parties (including, for example, providers of external services like web 
          traffic analysis services) may also use cookies, over which we have no control. These cookies 
          are likely to be analytical/performance cookies or targeting cookies.
        </Body>

        <SectionHeading>4. Managing Your Cookie Preferences</SectionHeading>
        <Body>
          When you first visit our Site, you will be presented with a cookie banner allowing you to accept 
          or manage your cookie preferences. You can change your preferences at any time by clearing your 
          browser cookies and revisiting the Site.
        </Body>
        <Body>
          Most web browsers allow some control of most cookies through the browser settings. To find out 
          more about cookies, including how to see what cookies have been set and how to manage and delete 
          them, visit www.allaboutcookies.org or www.aboutcookies.org.
        </Body>

        <SectionHeading>5. Changes to This Cookies Policy</SectionHeading>
        <Body>
          We may update this Cookies Policy from time to time to reflect changes to the cookies we use 
          or for other operational, legal, or regulatory reasons. Please revisit this Cookies Policy regularly 
          to stay informed about our use of cookies and related technologies.
        </Body>

        <SectionHeading>6. Contact Us</SectionHeading>
        <Body>
          If you have any questions about our use of cookies or this Cookies Policy, please contact us through 
          the Contact page on this Site or write to Velocity Offshore Holdings LLC, Majuro, Marshall Islands.
        </Body>
      </div>
    </div>
  </Layout>
);

export default Cookies;
